import { Controller, useForm } from "react-hook-form";
import {
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Form,
  FormGroup,
  Label,
  FormFeedback,
  Spinner,
} from "reactstrap";
import { CustomModal } from "../../../../../../components/addressManagement/addAddress/addAddressModal.style";
import { CloseGrayedIcon } from "../../../../../../assets/icons/close";
import { memo, useEffect, useState } from "react";
import { useApi } from "../../../../../../hooks/api.hook";
import { AvailableCouponsWrapper } from "../../../../../settings/subpages/refer-and-earn/sections/coupon-center/coupon-center.style";
import { CustomHelper } from "../../../../../../helpers/custom-helper";

export const AddDiscountCodeModalSection = memo(
  ({ isOpen, toggle, onApplyCode }) => {
    const [availableCoupons, setAvailableCoupons] = useState([]);
    const [loadingCoupons, setLoadingCoupons] = useState(false);
    const api = useApi();

    const {
      control,
      handleSubmit,
      formState: { errors, isSubmitting },
      reset,
      setError,
      watch,
      setValue,
      trigger,
    } = useForm({
      mode: "onChange",
      defaultValues: {
        discount_code: "",
      },
    });

    const discountCodeValue = watch("discount_code");

    useEffect(() => {
      const fetchAvailableCoupons = async () => {
        if (!isOpen) return;

        setLoadingCoupons(true);
        try {
          const { data: response } = await api.get("/referral_code/redeem");
          setAvailableCoupons(response?.data || []);
        } catch (error) {
          setAvailableCoupons([]);
        } finally {
          setLoadingCoupons(false);
        }
      };

      fetchAvailableCoupons();
    }, [isOpen, api]);

    const onSubmit = async (data) => {
      try {
        await onApplyCode(data.discount_code.trim());
        reset();
        toggle();
      } catch (error) {
        setError("root", {
          type: "manual",
          message: error.message || "Failed to apply referral code",
        });
      }
    };

    const handleModalToggle = () => {
      reset();
      toggle();
    };

    const handleApplyCoupon = async (couponCode) => {
      setValue("discount_code", couponCode);

      const isValid = await trigger("discount_code");

      if (isValid) {
        handleSubmit(onSubmit)();
      }
    };

    const getMatchingCoupon = () => {
      if (!discountCodeValue || availableCoupons.length === 0) return null;
      return availableCoupons.find(
        (coupon) =>
          coupon.code?.toLowerCase() === discountCodeValue.toLowerCase()
      );
    };

    const matchingCoupon = getMatchingCoupon();

    return (
      <CustomModal isOpen={isOpen} toggle={handleModalToggle} centered>
        <ModalHeader toggle={handleModalToggle}>
          <div className="close-btn" onClick={toggle}>
            <CloseGrayedIcon />
          </div>
          <span>Coupon Code</span>
        </ModalHeader>
        <Form onSubmit={handleSubmit(onSubmit)} noValidate>
          <ModalBody>
            <FormGroup style={{ marginBottom: ".25rem!important" }}>
              <Label for="discount_code" className="tw-font-bold">
                Enter your valid coupon code for a discount
              </Label>
              <Controller
                name={"discount_code"}
                control={control}
                rules={{
                  required: "Discount code is required",
                  minLength: {
                    value: 3,
                    message: "Discount code must be at least 3 characters",
                  },
                  pattern: {
                    value: /^[a-zA-Z0-9_-]+$/,
                    message:
                      "Discount code can only contain letters, numbers, hyphens and underscores",
                  },
                  validate: {
                    notEmpty: (value) =>
                      value.trim().length > 0 ||
                      "Discount code cannot be empty",
                  },
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="text"
                    id="discount_code"
                    name="discount_code"
                    placeholder="Enter your coupon code"
                    autoFocus
                    disabled={isSubmitting}
                    className="tw-border tw-border-gray-300 tw-rounded-lg tw-p-3"
                    invalid={!!errors.discount_code}
                  />
                )}
              ></Controller>
              <FormFeedback>{errors.discount_code?.message}</FormFeedback>
            </FormGroup>
            {matchingCoupon && (
              <div className="mb-3 tw-bg-green-50/65 tw-border tw-border-green-200 tw-rounded-lg tw-p-2 tw-mt-2">
                <div className="tw-font-semibold  tw-text-base tw-text-green-800">
                  {matchingCoupon.name}
                </div>
                {matchingCoupon.description && (
                  <div className="tw-text-green-700 tw-text-sm tw-mt-1">
                    {matchingCoupon.description}
                  </div>
                )}
                <div className="tw-text-green-600 tw-text-xs tw-mt-1">
                  Valid until:{" "}
                  {CustomHelper.formatDateToReadable(
                    new Date(matchingCoupon.valid_until)
                  )}
                </div>
              </div>
            )}
          </ModalBody>
          <ModalFooter className="tw-border-t tw-pt-4">
            <Button
              color="primary"
              type="submit"
              disabled={isSubmitting}
              className="tw-bg-blue-600 tw-border-blue-600"
            >
              {isSubmitting ? "Applying..." : "Apply Code"}
            </Button>
          </ModalFooter>
          <ModalBody>
            {availableCoupons.length > 0 && (
              <AvailableCouponsWrapper
                className={`tw-space-y-4 ${
                  loadingCoupons ? "bg-transparent" : ""
                }`}
              >
                <h4 className="title">Your available coupons</h4>
                <div className="custom-divider"></div>
                {loadingCoupons ? (
                  <div className="tw-text-gray-500 tw-text-sm d-flex align-items-center justify-content-center">
                    <Spinner size="sm" className="ms-2" />
                  </div>
                ) : (
                  availableCoupons.map((item, index) => (
                    <div
                      key={index}
                      className="outer d-flex align-items-center flex-nowrap justify-content-between"
                    >
                      <div>
                        <h5 className={"mb-1"}>{item?.description}</h5>
                        <p>
                          Valid Until:{" "}
                          {CustomHelper.formatDateToReadable(
                            new Date(item?.valid_until)
                          )}
                        </p>
                        <p className="tw-text-sm tw-text-gray-600">
                          Code: <strong>{item?.code}</strong>
                        </p>
                      </div>
                      <Button
                        onClick={() => handleApplyCoupon(item?.code)}
                        outline
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Applying..." : "Apply"}
                      </Button>
                    </div>
                  ))
                )}
              </AvailableCouponsWrapper>
            )}

            {errors.root && (
              <div className="tw-text-red-600 tw-text-sm tw-mt-2 tw-p-2 tw-bg-red-50 tw-rounded">
                {errors.root.message}
              </div>
            )}
          </ModalBody>
        </Form>
      </CustomModal>
    );
  }
);
